import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {createRef, ref} from 'lit/directives/ref.js';
import store from '../../../store';

import {getValueOfFeature, pimText, pimTextDebug} from '../../../util/values';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {conditional, genericConditional} from '../../../util/conditional';
import {
	selectCRI, selectDiameter, selectIsOnesided,
	selectIsSingle,
	selectSystemLumen,
	selectSystemLumenPerPower,
	selectSystemLumenString,
	selectSystemPower,
	selectSystemPowerString, selectWeight, selectWindsurfacearea, selectVariantCodeTypes, selectSpcConfiguration, selectRegion
} from '../../../selectors';
import {saveTenderText} from '../../../slices/configurationSlice';

@customElement('line-tender-text')
export class LineTenderText extends connect(store)(LitElement) {

	static styles = css`
		:host {
			container-type: inline-size;
			font: var(--font-specsheet-print-small-text);
		}

		p {
			margin: 0 0 1cqw 0;
			font: var(--font-specsheet-print-small-text);
			font-size: 0.92cqw;
			line-height: 140%;
		}

		.code {
			font: var(--font-specsheet-print-small-text);
			font-size: 0.6cqw;
		}
		.label {
			font: var(--font-specsheet-print-small-bold-text);
			font-size: 0.6cqw;
		}
	`;

	tenderTextRef = createRef();

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.lang = state.status.lang;
		this.state = state;
		this.debug = state.status.debug;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;


		this.typeAllocation = state.productData.variantCodeTypeTemplate.TypeAllocation;
		this.valueMappings = state.productData.variantCodeTypeTemplate.ValueMappings;
		this.definition = state.productData.variantCodeTypeTemplate.Definition;
		this.types = genericConditional(this.spcConfiguration, this.typeAllocation);
		this.values = genericConditional(this.spcConfiguration, this.valueMappings);

		this.templateType = null;
		this.codes = [];
		if (this.types && this.types.length > 0) {
			this.types.forEach((type) => {
				this.templateType = type.TemplateType;
				const definition = this.definition.find((def) => def.Type === this.templateType);
				//console.log('this.templateType', this.templateType, this.definition, definition);

				if (definition) {
					const template = definition.Template;
					const name = definition.Name;
					//console.log('ltt', definition, this.valueMappings, this.values);

					// template: "{VC_ArticleCode}{VC_HousingShape}{VC_Height}{VC_ModuleSize_1}{VC_ModuleSize_2}{VC_NumberPoleDoors}{VC_Grounding}{VC_SmartControl_1}{VC_EBlockMountingType} - {VC_ArticleCodeLightModule_1}{VC_HousingShape}{VC_ModuleSize_1}{VC_ColourTemperature_1}{VC_LightDistribution_1}{VC_OpticalVersion_1}{VC_ReflectorColour_1}{VC_Control_1}{VC_ElectricalProtectionClass_1}{VC_MountingLocationEBlock_1}{VC_SmartControl_1}{VC_CLT_1}{VC_ArticleCodeLightModule_2: -  {VC_ArticleCodeLightModule_1}}{VC_HousingShape}{VC_ModuleSize_2}{VC_ColourTemperature_2}{VC_LightDistribution_2}{VC_OpticalVersion_2}{VC_ReflectorColour_2}{VC_Control_2}{VC_ElectricalProtectionClass_2}{VC_MountingLocationEBlock_2}{VC_SmartControl_2}{VC_CLT_2}"
					// example from valuemapping: { "TemplateAttributeId": "VC_HousingColour", "TemplateAttributeValue": "A01" }

					// replace all placeholders in curly brackets with TemplateAttributeValue from value mapping based on TemplateAttributeId
					const code = template.replace(/{(.*?)}/g, (match, p1) => {
						const valueMapping = this.values.find((mapping) => mapping.TemplateAttributeId === p1);
						return valueMapping ? valueMapping.TemplateAttributeValue : match;
					});

					this.codes.push({
						name: name,
						code: code,
						template: template
					});
				}
			});
		}
		//console.log('codes', this.codes);
	}

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('copyToClipboard', () => this.copyToClipboard());
	}

	disconnectedCallback() {
		window.removeEventListener('copyToClipboard', () => this.copyToClipboard());
		super.disconnectedCallback();
	}
	firstUpdated(changedProperties) {
		const tenderText = this.tenderTextRef.value.innerText;
		const cleanedText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		store.dispatch(saveTenderText({tenderText: cleanedText}));
	}
	copyToClipboard() {
		let tenderText = this.tenderTextRef.value.innerText;
		// clean lineBreaks and spaces
		tenderText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		navigator.clipboard.writeText(tenderText).then(
			() => {},
			(err) => {
				console.error('Could not copy text: ', err);
			});
	}

	// shortcut for translated text
	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		const s = this.state;
		return html`
			<div ${ref(this.tenderTextRef)}>
				<p>
					Line
					${when(!selectIsSingle(s),
						() => html`
							${filterAndJoin([
								this.t('shared::ProductType::HS_HousingType', 'FV'),
								this.t('HS_HousingShape', 'FV'),
								this.t('shared::ProductType::HS_HousingType', 'FV.L'),
								this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
								this.t('LU_LuminaireDescription', 'D.L')
							], ', ')}${this.t('root::Poles::Pole::SpecialLine::HS_Construction', 'F', null, ', ', ':')}
							${filterAndJoin([
								this.t('root::Poles::Pole::SpecialLine::HS_Construction', 'FV.S'),
								this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature', 'F') +
								this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature', 'FV.S', null, ' ') +
								this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_ColourTemperature', 'FV.S', null, ' + ')
							], ', ')}
						`,
						() => html`
							${filterAndJoin([
								this.t('shared::ProductType::HS_HousingType', 'FV'),
								this.t('HS_HousingShape', 'FV'),
								this.t('shared::ProductType::HS_HousingType', 'FV.L'),
								this.t('LU_LuminaireDescription', 'D.L'),
								this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
								this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature', 'FV.S')
							], ', ')}
						`)}
				</p>
				<p>
					${filterAndJoin([
						this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature', 'F', null, '', ' LED ') +
						this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature', 'FV') + this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_ColourTemperature', 'FV', null, ' + '),

						this.t('LU_SystemLuminousFlux', 'D', null, '', ' ') +
						(selectSystemLumen(s) +
							this.t('LU_SystemLuminousFluxUnit.LM', 'DV.S', null, ' ', ' ') +
							(!selectIsOnesided(s) ? ' (' + selectSystemLumenString(s) + this.t('LU_SystemLuminousFluxUnit.LM', 'DV.S', null, ' ') + ')' : '')
						),

						this.t('LU_SystemWattage', 'D', null, '', ' ') +
						(selectSystemPower(s) +
							this.t('LU_SystemWattageUnit.W', 'DV.S', null, ' ', ' ') +
							(!selectIsOnesided(s) ? ' (' + selectSystemPowerString(s) + this.t('LU_SystemWattageUnit.W', 'DV.S', null, ' ') + ')' : '')
						),

						this.t('LU_Efficacy', 'D', null, '', ' ') +
						(selectSystemLumenPerPower(s) + this.t('LU_EfficacyUnit', 'D', null, ' ', '')),

						this.t('LE_CRI', 'D', null, '', ' ') + this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV'),
					], ', ')}
				</p>
				<p>
					${this.t('LE_LightEngineDescription', 'D', null, '', ':')}
					${filterAndJoin([
						this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'FV.L') + this.t('root::Heads::Head_2:Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'FV.L', null, ' + '),
						this.t('root::LightEngine_1::LE_UpLight', 'FV.L', null, ''),
						selectRegion(s) === 'fra' ? this.t('shared::Context::HS_LightEmissionCAFilter', 'FV.S', null, '') : '',
					], ', ')}
				</p>
				<p>
					${this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'F.S', null, '', ':')}
					${filterAndJoin([
						this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'FV.S'),
						this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightDistribution', 'FV.S'),
						this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightDistribution', 'FV.L'),
						this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_OpticalVersion', 'FV.S'),
						this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_ReflectorColour', 'FV.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_ReflectorMaterial'), 'DV.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L'),
					], ', ')}
				</p>
				${when(!selectIsOnesided(s), () => html`
					<p>
						${this.t('root::Heads::Head_2:Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'F.S', null, '', ':')}
						${filterAndJoin([
							this.t('root::Heads::Head_2:Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'FV.S'),
							this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_LightDistribution', 'FV.S'),
							this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_LightDistribution', 'FV.L'),
							this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_OpticalVersion', 'FV.S'),
							this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_ReflectorColour', 'FV.L'),
							this.t(this.c('DescriptiveAttributeReference.LE_ReflectorMaterial'), 'DV.L'),
							this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L'),
						], ', ')}
					</p>
				`)}
				<p>
					${this.t('root::Heads::Head::SmartControl::HS_HousingZhaga', 'F', null, '', ':')}
					${this.t('root::Heads::Head::SmartControl::HS_HousingZhaga', 'FV')}
				</p>
				<p>
					${this.t('root::Heads::Head::EBlock::EB_Control', 'F', null, '', ':')}
					${this.t('root::Heads::Head::EBlock::EB_Control', 'FV.S')}
					${this.t('root::Heads::Head::EBlock::EB_Control', 'FV.L')}
				</p>
				<p>
					${this.t('root::Heads::Head::EBlock::EB_ConstantLumenTechnology', 'F', null, '', ':')}
					${this.t('root::Heads::Head::EBlock::EB_ConstantLumenTechnology', 'FV.S')}
				</p>
				<p>
					${this.t('LU_SurgeProtection', 'D')}
					${this.t('LU_SurgeProtection.10kV', 'DV.S', null, '', ',')}
					${this.t('LU_TechnicalRegion', 'D')}
					${this.t('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV', null, '', ',')}
				</p>
				<p>
					${filterAndJoin([
						this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
						this.t('root::Poles::Pole::Basic::HS_Base', 'FV.L', null, ' ', ''),
						this.t('root::Poles::Pole::Basic::HS_NumberPoleDoors', 'FV.S'),
						this.t('root::Poles::Pole::SpecialLine::HS_HousingMountingLocationEBlock_1', 'F', null, '', ': ') + this.t('root::Heads::Head::EBlock::EB_EBlockMountingType', 'FV.S'),
						!selectIsOnesided(s) ? this.t('root::Poles::Pole::SpecialLine::HS_HousingMountingLocationEBlock_2', 'F', null, '', ': ') + this.t('root::Heads::Head_2::EBlock::EB_EBlockMountingType', 'FV.S') : '',
						this.t('root::Heads::Head::Housing::HS_HousingConnection', 'FV.S') + this.t('root::Heads::Head::Housing::HS_HousingConnection', 'FV.L', null, ' '),
						this.t(this.c('DescriptiveAttributeReference.LU_FuseBox'), 'DV.L'),
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('root::Heads::Head::ColourCoatingBase::HS_HousingColour', 'F', null, '', ': ') +
						this.t('root::Heads::Head::ColourCoatingBase::HS_HousingColour', 'FV'),
						this.t('root::Heads::Head::ColourCoatingBase::HS_HousingCoating', 'F', null, '', ': ') +
						this.t('root::Heads::Head::ColourCoatingBase::HS_HousingCoating', 'FV'),
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('root::Poles::Pole::Basic::HS_Height', 'F') +
						this.t('root::Poles::Pole::Basic::HS_Height', 'FV', null, ' ',),

						this.t('HS_HousingDiameter', 'D', null, ' ') +
						' 114 ' + // this.t('HS_HousingDiameter.114', 'DV.S', null, ' ') +
						this.t('HS_HousingDiameterUnit.MM', 'DV.S', null, ' '),

						this.t('LU_LuminaireWeight', 'D', null, ' ', ' ') +
						selectWeight(s) +
						this.t('LU_LuminaireWeightUnit', 'D', null, ' '),
					], ', ')}
					,<trans-label slot="label" key="HS_WindSurfaceArea" context="D"></trans-label> ${selectWindsurfacearea(s)} m<sup>2</sup>
				</p>
				<p>
					<!-- join -->
					${filterAndJoin([
						this.t(this.c('DescriptiveAttributeReference.LU_IP'), 'DV'),
						this.t(this.c('DescriptiveAttributeReference.LU_IK'), 'DV'),
						this.t('root::Heads::Head::EBlock::EB_ElectricalProtectionClass', 'FV.S'),
						this.t(this.c('DescriptiveAttributeReference.LU_ENEC_CertificateDescription'), 'DV'),
						this.t(this.c('DescriptiveAttributeReference.LU_CE'), 'DV'),
					], ', ')}
				</p>
				<p>
					${repeat(this.codes, (code) => html`
						<span class="label">${code.name}:</span>
						<span class="code">${code.code}</span><br>
					`)}
				</p>
				<p>
					<br>
					${this.t('TE_Manufacturer', 'D', null, '', ':')}
					Selux
				</p>

				${when(this.state.status.features.variantCodes,
					() => html`
						<p class="info">
							${repeat(
								selectVariantCodeTypes(s),
								(variantCode) => html`${variantCode.id}: ${variantCode.code}<br>`
							)}
						</p>`
				)}
				<p>
					<trans-label .key="${this.c('DescriptiveAttributeReference.LU_Accessories')}" context="DV.L"></trans-label>
				</p>
			</div>
		`;
	}
}
